import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { RbacPermissionsConstant } from '../../../services/rbac/rbac-permissions.constant';
import { AffiliateAccessFormGroup } from './affiliate-access-form';

interface List {
  id: string;
  name: string;
}

@Component({
  selector: 'gtd-affiliate-access',
  templateUrl: './affiliate-access.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AffiliateAccessComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class AffiliateAccessComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() control: UntypedFormControl;

  public allowedListArr: List[] = [];
  public deniedListArr: List[] = [];
  public form = new AffiliateAccessFormGroup();

  public data: Record<string, string>;
  public allowedData$ = new Subject();
  public deniedData$ = new Subject();
  private subscribers: Record<string, Subscription> = {};
  public RbacPermissionsConstant = RbacPermissionsConstant;

  writeValue(): void {}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  public onChange: (value: any) => void = () => {};
  public onTouch = () => {};

  ngOnInit(): void {
    const toSelectFormat = (id: string): List => ({ id, name: this.data[id] });
    const filterSelectData = (list: List[]): Record<string, string> => {
      const value = JSON.parse(JSON.stringify(this.data));
      for (const { id } of list) delete value[id];
      return value;
    };

    this.control.valueChanges.pipe(takeWhile((value) => Boolean(value?.length))).subscribe((value) => {
      this.allowedListArr = value.filter(({ type }) => +type).map(({ id }) => toSelectFormat(id));
      this.deniedListArr = value.filter(({ type }) => !+type).map(({ id }) => toSelectFormat(id));
      this.form.controls.allowed.patchValue(this.allowedListArr.map(({ id }) => id));
      this.form.controls.denied.patchValue(this.deniedListArr.map(({ id }) => id));
    });

    this.subscribers.allowed = this.form.controls.allowed.valueChanges.subscribe((id: string[]) => {
      this.allowedListArr = id.map(toSelectFormat);
      this.deniedData$.next(filterSelectData(this.allowedListArr));
    });
    this.subscribers.denied = this.form.controls.denied.valueChanges.subscribe((id: string[]) => {
      this.deniedListArr = id.map(toSelectFormat);
      this.allowedData$.next(filterSelectData(this.deniedListArr));
    });
    this.subscribers.form = this.form.valueChanges
      .pipe(
        map((value: { allowed: number[]; denied: number[] }) => {
          const toBackendFormat = (id: number, type: 0 | 1) => ({ id, type });
          const allowed = value.allowed.map((id) => toBackendFormat(id, 1));
          const denied = value.denied.map((id) => toBackendFormat(id, 0));
          return [...allowed, ...denied];
        })
      )
      .subscribe((value) => {
        this.control.patchValue(value, { onlySelf: true, emitEvent: false });
        this.control.markAsTouched();
        this.control.markAsDirty();
      });
  }

  ngAfterViewInit(): void {
    this.allowedData$.next(this.data);
    this.deniedData$.next(this.data);
  }

  remove(index: number, list: List[]): void {
    list.splice(index, 1);
    this.updateSelectForms();
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    else transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    this.updateSelectForms();
  }

  private updateSelectForms(): void {
    this.form.controls.allowed.patchValue(this.allowedListArr.map(({ id }) => id));
    this.form.controls.denied.patchValue(this.deniedListArr.map(({ id }) => id));
  }
}
